import { useEffect, useState } from 'react';
import { openSnackbar } from '../api/snackbar';
import { useUserContext } from '../contexts/UserContext';
import fetchWithRefresh from '../utils/fetchWithRefresh';
import { toast } from 'react-toastify';
import {
  Stack,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  DialogActions,
  Typography,
  Button
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

export default function ContactModal({ contactModal, setContactModal }) {
  const { selectedVenueData, logout, updateTokens, user } = useUserContext();
  const [userDetails, setUserDetails] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchProfile = async () => {
    try {
      const req = await fetchWithRefresh(
        import.meta.env.VITE_API_URL +
          `Users/FetchUserAccountDetails?UserLoginId=${user?.id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.token}`
          }
        },
        logout,
        updateTokens,
        (message) => {
          toast.info(message);
        }
      );

      if (req.status === 200) {
        const data = await req.json();
        setUserDetails((prev) => {
          const isSame = JSON.stringify(prev) === JSON.stringify(data);
          return isSame ? prev : data;
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setHasFetched(true);
    }
  };

  useEffect(() => {
    if (!user || hasFetched) return;
    fetchProfile();
  }, [user, hasFetched]);

  const handleContactEnquiry = async (values, { setSubmitting }) => {
    setSubmitting(true);

    const formData = {
      name: values.name ?? '',
      email: values.email ?? '',
      organisation: values.organisation ?? '',
      comments: values.comments ?? ''
    };

    try {
      const req = await fetchWithRefresh(
        import.meta.env.VITE_API_URL + `formhandler/ProcessPortalForm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.token}`
          },
          body: JSON.stringify(formData)
        },
        logout,
        updateTokens,
        (message) => {
          toast.info(message);
        }
      );

      if (req.status === 200) {
        setContactModal({ open: false });

        openSnackbar({
          open: true,
          message: 'Request successfully sent.',
          variant: 'alert',
          alert: {
            color: 'success'
          }
        });
      } else {
        const error = await req.text();
        openSnackbar({
          open: true,
          message: error,
          variant: 'alert',
          alert: {
            color: 'error'
          }
        });
      }
    } catch (error) {
      openSnackbar({
        open: true,
        message: `Request could not be sent, please try again.`,
        variant: 'alert',
        alert: {
          color: 'error'
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: userDetails ? `${userDetails?.FirstName} ${userDetails?.LastName}` : '',
      organisation: selectedVenueData?.orgPartnerFriendlyName ?? '',
      email: userDetails ? userDetails?.Email : '',
      comments: ''
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required('You must enter a name.'),
      organisation: Yup.string().required('You must enter an organisation name.'),
      email: Yup.string().required('You must enter an email address.'),
      comments: Yup.string().required('You must add a comment or question.')
    }),
    onSubmit: handleContactEnquiry
  });

  const {
    handleSubmit,
    handleBlur,
    handleChange,
    values,
    errors,
    touched,
    isSubmitting
  } = formik;

  return (
    <Dialog
      open={contactModal.open}
      onClose={() => setContactModal({ open: false })}
      sx={{ maxWidth: '600px', margin: '0 auto' }}
    >
      <Grid size={12}>
        <Stack spacing={1}>
          <DialogTitle variant="h2" sx={{ fontWeight: 600, fontSize: '1.875rem' }}>
            Get In Touch
          </DialogTitle>
          <DialogContent sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
            <DialogContentText>
              We're happy to answer any questions you might have or help you with the app. Just
              fill in the form below and one of our team will be in touch or call us on 0800 999
              3669.
            </DialogContentText>
            <DialogContentText>
              Our offices are open Monday to Friday between 9am and 4.30pm.
            </DialogContentText>
          </DialogContent>

          <div style={{ paddingLeft: '24px' }}>
            <form noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={12} sx={{ paddingRight: '24px' }}>
                  <Stack sx={{ gap: 1 }}>
                    <InputLabel htmlFor="name">
                      <Typography variant="h5">Name</Typography>
                    </InputLabel>
                    <OutlinedInput
                      id="name"
                      value={values.name}
                      name="name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      fullWidth
                      error={Boolean(touched.name && errors.name)}
                    />
                  </Stack>
                  {touched.name && errors.name && (
                    <FormHelperText error id="standard-weight-helper-text-name">
                      {errors.name}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid size={12} sx={{ paddingRight: '24px' }}>
                  <Stack sx={{ gap: 1 }}>
                    <InputLabel htmlFor="organisation">
                      <Typography variant="h5">Organisation Name</Typography>
                    </InputLabel>
                    <OutlinedInput
                      id="organisation"
                      value={values.organisation}
                      name="organisation"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      fullWidth
                      error={Boolean(touched.organisation && errors.organisation)}
                    />
                  </Stack>
                  {touched.organisation && errors.organisation && (
                    <FormHelperText error id="standard-weight-helper-text-organisation">
                      {errors.organisation}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid size={12} sx={{ paddingRight: '24px' }}>
                  <Stack sx={{ gap: 1 }}>
                    <InputLabel htmlFor="email">
                      <Typography variant="h5">Email Address</Typography>
                    </InputLabel>
                    <OutlinedInput
                      id="email"
                      value={values.email}
                      name="email"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      fullWidth
                      error={Boolean(touched.email && errors.email)}
                    />
                  </Stack>
                  {touched.email && errors.email && (
                    <FormHelperText error id="standard-weight-helper-text-email">
                      {errors.email}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid size={12} sx={{ paddingRight: '24px' }}>
                  <Stack sx={{ gap: 1 }}>
                    <InputLabel htmlFor="comments">
                      <Typography variant="h5">Comments &amp; Questions</Typography>
                    </InputLabel>
                    <OutlinedInput
                      id="comments"
                      type="text"
                      multiline
                      value={values.comments}
                      name="comments"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      fullWidth
                      error={Boolean(touched.comments && errors.comments)}
                    />
                  </Stack>
                  {touched.comments && errors.comments && (
                    <FormHelperText error id="standard-weight-helper-text-comments">
                      {errors.comments}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid size={12}>
                  <DialogActions>
                    <Button
                      onClick={() => setContactModal({ open: false })}
                      variant="contained"
                      color="secondary"
                    >
                      Cancel
                    </Button>
                    <Button
                      disableElevation
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Request'}
                    </Button>
                  </DialogActions>
                </Grid>
              </Grid>
            </form>
          </div>
        </Stack>
      </Grid>
    </Dialog>
  );
}